import { Delete, Upload } from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { getAllProduct } from "../../apis/Product";
import {
  COLLECTION_TYPES,
  createPopupCollection,
  getPopups,
  updatePopupCollection,
  uploadPopupCollectionImage,
} from "../../apis/PopupCollection";
import { showToast } from "../../utils/toast";
import FormWrapper from "../Form/FormWrapper";

// The API may return related fields either as ids or as nested objects
const toOption = (value, labelKey) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "object") {
    return value;
  }
  return { id: value, [labelKey]: `#${value}` };
};

// Async-search autocomplete shared by the popup and product pickers
const useAsyncOptions = (fetcher) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useMemo(
    () =>
      debounce(async (query) => {
        try {
          setLoading(true);
          setOptions(await fetcher(query));
        } catch (error) {
          console.error(error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 500),
    [fetcher],
  );

  useEffect(() => () => search.cancel(), [search]);

  return { options, loading, search };
};

const fetchPopups = async (query) => (await getPopups(query)).results || [];

const fetchProducts = async (query) => {
  if (!query) {
    return [];
  }
  return (await getAllProduct(10, 1, { name: query })).results || [];
};

const loadingAdornment = (params, loading) => ({
  ...params.InputProps,
  endAdornment: (
    <>
      {loading && <CircularProgress size={20} />}
      {params.InputProps.endAdornment}
    </>
  ),
});

export const PopupCollectionDrawer = ({
  collection,
  setEditOpen,
  reloadTable,
  setLoading,
}) => {
  // =========================
  // Form
  // =========================

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      popup: null,
      collection_type: "edit",
      products: [],
      display_order: 0,
      active: true,
    },
  });

  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const popupSearch = useAsyncOptions(fetchPopups);
  const productSearch = useAsyncOptions(fetchProducts);

  // =========================
  // Effects
  // =========================

  useEffect(() => {
    reset({
      name: collection?.name || "",
      popup: toOption(collection?.popup, "title"),
      collection_type: collection?.collection_type || "edit",
      products: (collection?.products || []).map((p) => toOption(p, "name")),
      display_order: collection?.display_order ?? 0,
      active: collection?.active ?? true,
    });
    setImage(toOption(collection?.image, "file"));
  }, [collection, reset]);

  useEffect(() => {
    popupSearch.search("");
  }, [popupSearch]);

  // =========================
  // Handlers
  // =========================

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }

    setUploading(true);

    try {
      setImage(await uploadPopupCollectionImage(file));
    } catch (error) {
      console.error(error);
      showToast("Failed to upload image", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (data) => {
    setLoading(true);

    const payload = {
      name: data.name,
      popup: data.popup.id,
      collection_type: data.collection_type,
      products: data.products.map((product) => product.id),
      image: image?.id ?? null,
      display_order: Number(data.display_order) || 0,
      active: data.active,
    };

    try {
      if (collection) {
        await updatePopupCollection(collection.id, payload);
      } else {
        await createPopupCollection(payload);
      }

      await reloadTable();

      setEditOpen(false);

      showToast(
        collection
          ? "Bucket updated successfully"
          : "Bucket added successfully",
        "success",
      );
    } catch (error) {
      console.error(error);

      showToast(
        collection ? "Failed to update bucket" : "Failed to add bucket",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Render
  // =========================

  return (
    <FormWrapper onSubmit={handleSubmit(handleSave)} sx={{ p: 5 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">
          {collection ? "Edit Popup Bucket" : "Create Popup Bucket"}
        </Typography>

        <Button type="submit" variant="contained" disabled={uploading}>
          {collection ? "Update" : "Save"}
        </Button>
      </Box>

      {/* Popup */}
      <Box mb={3}>
        <Controller
          name="popup"
          control={control}
          rules={{ required: "Popup is required" }}
          render={({ field }) => (
            <Autocomplete
              options={popupSearch.options}
              value={field.value}
              loading={popupSearch.loading}
              filterOptions={(x) => x}
              getOptionLabel={(option) => option?.title || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => field.onChange(value)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  popupSearch.search(value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Popup"
                  error={Boolean(errors.popup)}
                  helperText={errors.popup?.message}
                  InputProps={loadingAdornment(params, popupSearch.loading)}
                />
              )}
            />
          )}
        />
      </Box>

      {/* Name */}
      <Box mb={3}>
        <TextField
          fullWidth
          label="Name"
          inputProps={{ maxLength: 100 }}
          {...register("name")}
        />
      </Box>

      {/* Type */}
      <Box mb={3}>
        <Controller
          name="collection_type"
          control={control}
          render={({ field }) => (
            <TextField select fullWidth label="Bucket Type" {...field}>
              {COLLECTION_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>

      {/* Display Order */}
      <Box mb={3}>
        <TextField
          fullWidth
          type="number"
          label="Display Order"
          inputProps={{ min: 0 }}
          {...register("display_order", {
            min: { value: 0, message: "Display order cannot be negative" },
          })}
          error={Boolean(errors.display_order)}
          helperText={errors.display_order?.message}
        />
      </Box>

      {/* Image */}
      <Box mb={3} display="flex" alignItems="center" gap={2}>
        <Avatar
          variant="square"
          src={image?.file}
          sx={{ width: 80, height: 80, borderRadius: 1 }}
        />

        <Button
          component="label"
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <Upload />}
          disabled={uploading}
        >
          {image ? "Change Image" : "Upload Image"}
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
        </Button>

        {image && (
          <IconButton color="error" onClick={() => setImage(null)}>
            <Delete />
          </IconButton>
        )}
      </Box>

      {/* Products */}
      <Box mb={3}>
        <Controller
          name="products"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              options={productSearch.options}
              value={field.value}
              loading={productSearch.loading}
              filterSelectedOptions
              filterOptions={(x) => x}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => field.onChange(value)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  productSearch.search(value);
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} gap={1}>
                  <Avatar
                    variant="square"
                    src={option?.image?.file}
                    sx={{ width: 32, height: 40 }}
                  />
                  {option.name}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Products (${field.value.length})`}
                  placeholder="Search by product name"
                  InputProps={loadingAdornment(params, productSearch.loading)}
                />
              )}
            />
          )}
        />
      </Box>

      {/* Status */}
      <Box mb={3}>
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label={field.value ? "Active" : "Inactive"}
              control={
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  color="primary"
                />
              }
            />
          )}
        />
      </Box>
    </FormWrapper>
  );
};
